import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface CocoConversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface CocoMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

interface CocoContextValue {
  conversations: CocoConversation[];
  conversationsLoading: boolean;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  messages: CocoMessage[];
  messagesLoading: boolean;
  streamingContent: string;
  isStreaming: boolean;
  sendMessage: (prompt: string) => Promise<void>;
  createConversation: () => Promise<CocoConversation | null>;
  deleteConversation: (id: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
}

const CocoContext = createContext<CocoContextValue | undefined>(undefined);

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('support-app-token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const CocoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [conversations, setConversations] = useState<CocoConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [messages, setMessages] = useState<CocoMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const titleRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Full refresh with loading indicator (used on mount)
  const refreshConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const response = await fetch('/api/coco/conversations', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // Silent refresh — no loading state, used for background title updates
  const silentRefreshConversations = useCallback(async () => {
    try {
      const response = await fetch('/api/coco/conversations', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(Array.isArray(data) ? data : []);
      }
    } catch {}
  }, []);

  // Fetch single conversation detail and merge its title into state
  const refreshConversationDetail = useCallback(async (convId: string) => {
    try {
      const response = await fetch(`/api/coco/conversations/${convId}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const detail = await response.json();
        if (detail?.id) {
          setConversations((prev) =>
            prev.map((c) =>
              c.id === detail.id ? { ...c, title: detail.title, updated_at: detail.updated_at } : c,
            ),
          );
        }
      }
    } catch {}
  }, []);

  // Refresh titles: immediately + delayed 15s retry for async title generation
  const scheduleConversationTitleRefresh = useCallback(
    (convId: string) => {
      // Clear any existing timer
      if (titleRefreshTimerRef.current) {
        clearTimeout(titleRefreshTimerRef.current);
      }

      // Immediate refresh
      silentRefreshConversations();
      refreshConversationDetail(convId);

      // Delayed retry — backend generates title asynchronously
      titleRefreshTimerRef.current = setTimeout(() => {
        silentRefreshConversations();
        refreshConversationDetail(convId);
      }, 15000);
    },
    [silentRefreshConversations, refreshConversationDetail],
  );

  const createConversation = useCallback(async (): Promise<CocoConversation | null> => {
    try {
      const response = await fetch('/api/coco/conversations', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: 'New Chat' }),
      });
      if (response.ok) {
        const conversation = await response.json();
        setConversations((prev) => [conversation, ...prev]);
        setActiveConversationId(conversation.id);
        setMessages([]);
        return conversation;
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
    return null;
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      try {
        await fetch(`/api/coco/conversations/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        setConversations((prev) => prev.filter((c) => c.id !== id));
        if (activeConversationId === id) {
          setActiveConversationId(null);
          setMessages([]);
        }
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    },
    [activeConversationId],
  );

  const sendMessage = useCallback(
    async (prompt: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      let conversationId = activeConversationId;

      if (!conversationId) {
        const newConv = await createConversation();
        if (!newConv) return;
        conversationId = newConv.id;
      }

      const userMessage: CocoMessage = { role: 'user', content: prompt };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setStreamingContent('');

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const params = new URLSearchParams({
          conversation_id: conversationId,
          prompt,
        });
        const token = localStorage.getItem('support-app-token');
        const response = await fetch(`/api/coco/chat?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('Stream request failed');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true });
          const lines = text.split('\n');

          for (const line of lines) {
            if (line.startsWith('event: ') && line.includes('stream-complete')) {
              continue;
            }
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]' || data === '{}') continue;
              try {
                const decoded = atob(data);
                accumulated += decoded;
                setStreamingContent(accumulated);
              } catch {
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.content) {
                    accumulated += parsed.content;
                    setStreamingContent(accumulated);
                  }
                } catch {
                  if (data) {
                    accumulated += data;
                    setStreamingContent(accumulated);
                  }
                }
              }
            }
          }
        }

        if (accumulated) {
          const assistantMessage: CocoMessage = {
            role: 'assistant',
            content: accumulated,
          };
          setMessages((prev) => [...prev, assistantMessage]);
        }

        // Schedule title refresh (immediate + 15s delayed)
        scheduleConversationTitleRefresh(conversationId);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Chat stream error:', error);
        }
      } finally {
        setIsStreaming(false);
        setStreamingContent('');
        abortControllerRef.current = null;
      }
    },
    [activeConversationId, createConversation, scheduleConversationTitleRefresh],
  );

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchMessages = async () => {
      setMessagesLoading(true);
      setMessages([]);
      try {
        const response = await fetch(
          `/api/coco/conversations/${activeConversationId}/messages`,
          { headers: getAuthHeaders(), signal: controller.signal },
        );
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;
          const items = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
              ? data.items
              : [];
          const mapped: CocoMessage[] = items.map((msg: any) => ({
            id: msg.id,
            role: msg.message_type === 'user' || msg.sender === 'user'
              ? 'user'
              : 'assistant',
            content: msg.content || msg.text || '',
            created_at: msg.created_at,
          }));
          setMessages(mapped);
        } else if (response.status === 404) {
          if (!cancelled) setMessages([]);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch messages:', error);
        }
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    fetchMessages();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activeConversationId]);

  // Load conversations on mount + cleanup timer
  useEffect(() => {
    refreshConversations();
    return () => {
      if (titleRefreshTimerRef.current) {
        clearTimeout(titleRefreshTimerRef.current);
      }
    };
  }, [refreshConversations]);

  const value = useMemo(
    () => ({
      conversations,
      conversationsLoading,
      activeConversationId,
      setActiveConversationId,
      messages,
      messagesLoading,
      streamingContent,
      isStreaming,
      sendMessage,
      createConversation,
      deleteConversation,
      refreshConversations,
    }),
    [
      conversations,
      conversationsLoading,
      activeConversationId,
      messages,
      messagesLoading,
      streamingContent,
      isStreaming,
      sendMessage,
      createConversation,
      deleteConversation,
      refreshConversations,
    ],
  );

  return <CocoContext.Provider value={value}>{children}</CocoContext.Provider>;
};

export const useCoco = (): CocoContextValue => {
  const context = useContext(CocoContext);
  if (!context) {
    throw new Error('useCoco must be used within a CocoProvider');
  }
  return context;
};
